<div class="py-12 px-4 sm:px-6 lg:px-8">
  <div class="max-w-3xl mx-auto">
    <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">Privacy Policy</h1>
    <p class="text-gray-600 dark:text-gray-300 mb-4">Last Updated: May 22, 2025</p>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">1. Introduction</h2>
      <p class="text-gray-600 dark:text-gray-300">
        Welcome to {{ tradeName }}. We are committed to protecting your privacy and ensuring that your personal information is handled in a
        safe and responsible manner. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use
        our services.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">2. Information We Collect</h2>
      <p class="text-gray-600 dark:text-gray-300">
        We collect personal information such as your name, email address, and optionally your phone number when you use our services.
        Payment information is processed and stored by Stripe and is not retained by us.
      </p>
      <p class="text-gray-600 dark:text-gray-300 mt-2">
        We also collect information about {{ organisationAlias }} entities, including their name, description, and member lists (visible
        only to {{ organisationAlias }} admins and owners). Additionally, we may collect technical data (e.g., IP address, browser type) for
        analytics and personalization purposes, subject to your consent where required.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">3. Legal Basis for Processing</h2>
      <p class="text-gray-600 dark:text-gray-300">We process your personal data based on the following legal bases under GDPR Article 6:</p>
      <ul class="text-gray-600 dark:text-gray-300 mt-2 ml-4 list-disc">
        <li>
          <strong>Contract Performance (Art. 6(1)(b)):</strong> We process your name, email address, and account information as necessary to
          provide our services and fulfill our contractual obligations to you.
        </li>
        <li>
          <strong>Consent (Art. 6(1)(a)):</strong> We process analytics and tracking data (including third-party pixels) only with your
          explicit consent, which you can withdraw at any time.
        </li>
        <li>
          <strong>Legitimate Interest (Art. 6(1)(f)):</strong> We process technical data for security purposes and service improvement,
          where our legitimate interests do not override your privacy rights.
        </li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">4. How We Use Your Information</h2>
      <p class="text-gray-600 dark:text-gray-300">
        We use your information to provide, maintain, and improve our services, including for authentication, personalization within the
        app, and analytics features (with your consent). We do not use your data for marketing purposes.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">5. Third-Party Services</h2>
      <p class="text-gray-600 dark:text-gray-300">
        We use third-party services such as Firebase for authentication and data storage, Algolia for search functionality, and Stripe for
        payment processing. We may also use analytics services such as Google Analytics and Meta Pixel with your consent. These providers
        may have access to your data as necessary to perform their functions and are bound by their own privacy policies, which we encourage
        you to review. In the future, we may integrate additional safe third-party services, such as external APIs, and will update this
        policy accordingly.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">6. Cookies and Tracking</h2>
      <p class="text-gray-600 dark:text-gray-300">
        We use essential cookies for authentication (via Firebase), payment processing (via Stripe), and security (e.g., Google's
        _GRECAPTCHA cookie to prevent automated abuse). We also use essential cookies from Algolia for search functionality, all of which
        are necessary for the functionality and security of our services and do not require consent.
      </p>
      <p class="text-gray-600 dark:text-gray-300 mt-2">
        We also use analytics cookies and tracking pixels (such as Google Analytics and Meta Pixel) with your explicit consent. You can
        manage your cookie preferences and withdraw consent at any time through our cookie consent banner or by contacting us at
        {{ contactInfo.email }}.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">7. Data Retention</h2>
      <p class="text-gray-600 dark:text-gray-300">
        We retain your personal data only as long as necessary for the purposes for which it was collected:
      </p>
      <ul class="text-gray-600 dark:text-gray-300 mt-2 ml-4 list-disc">
        <li><strong>Active accounts:</strong> For the duration of your account plus 30 days after deletion to ensure complete removal.</li>
        <li><strong>Inactive accounts:</strong> For 12 months after last login to allow account recovery and provide support.</li>
        <li><strong>Deleted accounts:</strong> All data is erased immediately upon deletion, except where retention is required by law.</li>
        <li>
          <strong>Legal requirements:</strong> We may retain data longer if required by applicable laws, regulations, or legal proceedings.
        </li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">8. International Data Transfers</h2>
      <p class="text-gray-600 dark:text-gray-300">
        Your data may be transferred to and processed in countries outside the European Economic Area, including the United States (by
        Firebase, Algolia, Stripe, Google, and Meta). We ensure these transfers comply with applicable data protection laws, such as GDPR,
        through mechanisms like Standard Contractual Clauses and adequacy decisions where available.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">9. Your Rights and Choices</h2>
      <p class="text-gray-600 dark:text-gray-300">
        You may access, modify, or delete your personal information at any time through your account settings. If you are an EU resident,
        you also have the following rights under GDPR:
      </p>
      <ul class="text-gray-600 dark:text-gray-300 mt-2 ml-4 list-disc">
        <li><strong>Right to access:</strong> Request a copy of your personal data</li>
        <li><strong>Right to rectification:</strong> Correct inaccurate personal data</li>
        <li><strong>Right to erasure:</strong> Request deletion of your personal data</li>
        <li><strong>Right to restrict processing:</strong> Limit how we use your data</li>
        <li><strong>Right to object:</strong> Object to processing based on legitimate interests</li>
        <li><strong>Right to data portability:</strong> Receive your data in a portable format</li>
        <li><strong>Right to withdraw consent:</strong> Withdraw consent for analytics and tracking at any time</li>
      </ul>
      <p class="text-gray-600 dark:text-gray-300 mt-2">
        To exercise these rights, please contact us at {{ contactInfo.email }} or {{ contactInfo.phone.displayedNumber }}. You also have the
        right to lodge a complaint with your local data protection supervisory authority.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">10. Data Security and Breach Notification</h2>
      <p class="text-gray-600 dark:text-gray-300">
        We implement reasonable security measures, including 2-factor authentication, email sign-in, and Google authentication, to protect
        your data. However, no method is 100% secure.
      </p>
      <p class="text-gray-600 dark:text-gray-300 mt-2">
        In the event of a data breach that may result in a high risk to your rights and freedoms, we will notify the relevant supervisory
        authorities within 72 hours of becoming aware of the breach and will inform affected users without undue delay. If you have concerns
        about data security, reach out to us at {{ contactInfo.email }}.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">11. Contact Us</h2>
      <p class="text-gray-600 dark:text-gray-300">
        If you have any questions about this Privacy Policy or wish to exercise your data protection rights, please contact us at
        {{ contactInfo.email }} or {{ contactInfo.phone.displayedNumber }}.
      </p>
      <p class="text-gray-600 dark:text-gray-300 mt-2">
        For complaints about our data processing practices, you may also contact your local data protection supervisory authority.
      </p>
    </section>
  </div>
</div>
